import { gateOpensWords, type BuilderWho } from '@nsb/core';
import type { Band, BandTone } from './model.ts';

/**
 * Who does a step, as its band says it (design study 10, band B): one rule for every tree, the master, a job and the
 * client's, each in its reader's words. NSB in ink, a sub in slate with its trade, a supplier in ochre with its trade,
 * the client in bengara, outside in grey; a decision by who decides, a gate by who approves it, a milestone and the
 * ends pale. A gate nobody approves happens (a payment in) and is its diamond alone, with no word (Jason, 2026-10-06:
 * "just don't have anything for gates, maybe just a gate icon"). The client reads You, NSB and Others (client band A),
 * never a sub's or a supplier's name.
 */

/** Who reads the band: NSB on its own pages, or the client on theirs. */
export type BandReader = 'nsb' | 'client';

/** What a band is drawn from: the step's kind, who does it (decides it, approves it), and its trade. */
export interface BandFacts {
  kind: 'work' | 'decision' | 'gate' | 'milestone' | 'start' | 'end';
  who: BuilderWho | null;
  trade?: string | null;
  /** What an outside step is called, on a job: the authority. */
  outside?: string | null;
}

const NSB_TONE: Record<BuilderWho, BandTone> = { nsb: 'ink', sub: 'slate', supplier: 'ochre', client: 'bengara', outside: 'grey' };

/** Whose a step is, as the client reads it: theirs, NSB's (everything flows through NSB), or someone else's. */
const sideOf = (who: BuilderWho | null): 'you' | 'nsb' | 'others' => (who === 'client' ? 'you' : who === 'nsb' || who === null ? 'nsb' : 'others');
const CLIENT_TONE: Record<'you' | 'nsb' | 'others', BandTone> = { you: 'bengara', nsb: 'ink', others: 'slate' };
const CLIENT_WORDS = {
  gate: { you: 'You approve', nsb: 'NSB approves', others: 'Others approve' },
  decision: { you: 'You decide', nsb: 'NSB decides', others: 'Others decide' },
  work: { you: 'You', nsb: 'NSB', others: 'Others' },
} as const;

export function bandOf(s: BandFacts, reader: BandReader, tradeName: (key: string) => string | null = () => null): Band {
  if (s.kind === 'start' || s.kind === 'end') return { tone: 'pale', mark: 'end', word: s.kind === 'start' ? 'Start' : 'End' };
  if (s.kind === 'milestone') return { tone: 'pale', mark: 'milestone', word: 'Milestone' };
  if (s.kind === 'gate' && s.who === null) return { tone: 'ink', mark: 'gate', word: null };
  if (reader === 'client') {
    const side = sideOf(s.who);
    return { tone: CLIENT_TONE[side], mark: s.kind === 'work' ? null : s.kind, word: CLIENT_WORDS[s.kind][side] };
  }
  if (s.kind === 'gate') return { tone: NSB_TONE[s.who!], mark: 'gate', word: gateOpensWords(s.who!) };
  if (s.kind === 'decision') {
    return s.who === 'client'
      ? { tone: 'bengara', mark: 'decision', word: 'Client decides' }
      : s.who === 'nsb'
        ? { tone: 'ink', mark: 'decision', word: 'NSB decides' }
        : { tone: 'grey', mark: 'decision', word: 'Decision' };
  }
  const trade = s.trade ? (tradeName(s.trade) ?? s.trade) : null;
  switch (s.who) {
    case 'nsb':
      return { tone: 'ink', mark: null, word: 'NSB' };
    case 'sub':
      return { tone: 'slate', mark: null, word: trade ? `Sub · ${trade}` : 'Sub' };
    case 'supplier':
      return { tone: 'ochre', mark: null, word: trade ? `Supplier · ${trade}` : 'Supplier' };
    case 'client':
      return { tone: 'bengara', mark: null, word: 'Client' };
    case 'outside':
      return { tone: 'grey', mark: null, word: s.outside ?? 'Outside' };
    default:
      return { tone: 'grey', mark: null, word: 'Work' };
  }
}
